import { useState, useEffect, useRef } from "react";
import "./updateInfo.css";
import PartsLabourTable from "./PartsLabourTable";
import { useParams, useNavigate } from "react-router";
import axios from "axios";
import makeData from "./data.json";

// Location cache and function
const locationCache = new Map();

const getLocationFromCoords = async (lat, lng) => {
  if (!lat || !lng || lat === "null" || lng === "null") return "";

  const cacheKey = `${lat},${lng}`;
  if (locationCache.has(cacheKey)) {
    return locationCache.get(cacheKey);
  }

  try {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    if (isNaN(latNum) || isNaN(lngNum)) return "";

    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latNum}&lon=${lngNum}&zoom=18&addressdetails=1`,
    );

    if (!response.ok) return "";

    const data = await response.json();

    if (data && data.display_name) {
      const address = data.display_name;
      locationCache.set(cacheKey, address);
      return address;
    }
    return "";
  } catch (error) {
    console.error("Error fetching location:", error);
    return "";
  }
};

// SIMPLIFIED fetch function - sirf 2 values
const fetchCarInspectionData = async (claimNumber) => {
  try {
    console.log(`Fetching car inspection data for claim: ${claimNumber}`);

    const response = await fetch("/process_data/get_car_inspection_data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ claimNumber: claimNumber }),
    });

    const data = await response.json();
    console.log("116:", data);

    return {
      odometerImageValue: data.odometerNumber || "Not Found",

      chassisNumber: data.chassisNumber || "Not Found",
      rcNumber: data.rcNumber || "Not Found",
      engineNumber: data.ocr_engine_number || data.engineNumber || "Not Found",

      policyNumber: data.policyNumber || "Not Found",
      registrationNumber: data.registrationNumber || "Not Found",
      make: data.make || "Not Found",
      model: data.model || "Not Found",
      variant: data.variant || "Not Found",
      accidentDate: data.accidentDate || "Not Found",
      driverLicense: data.driverLicense || "Not Found",
      driverName: data.driverName || "Not Found",

      source: "api",
    };
  } catch (error) {
    console.error("Error:", error);
    return {
      chassisNumber: "Not Found",
      rcNumber: "Not Found",
      engineNumber: "Not Found",
      source: "error",
    };
  }
};

const UpdateInfoPage = () => {
  const [activeTab, setActiveTab] = useState("insurance");
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const { claimNumber } = useParams();
  const navigate = useNavigate();

  const [surveyorId, setSurveyorId] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // State to store car inspection data
  const [carInspectionData, setCarInspectionData] = useState(null);

  const [formData, setFormData] = useState({
    claimNumber: claimNumber,
    policyNumber: "",
    insurancePeriod: "",
    idv: "",
    endorsement: "",
    insuranceCompany: "",
    BodyType: "",
    date: "",
    insured: "",
    hpa: "",
    appointedBy: "",
    VehicleRegistrationNumber: "",
    RegisteredOwner: "",
    OwnerSerialNoTransferDate: "",
    DateofRegistration: "",
    ChassisNumber: "",
    EngineNumber: "",
    ChassisPhysicallyChecked: "",
    Variant: "",
    Model: "",
    Color: "",
    Make: "",
    CubicCapacity: "",
    FuelUsed: "",
    OdoMeterReading: "",
    TaxParticulars: "",
    PUCNumber: "",
    ValidUpto: "",
    NameofDriver: "",
    DateofBirth: "",
    SeatingCapacity: "",
    DrivingLicenseNo: "",
    DateofIssue: "",
    ValidFrom: "",
    ValidUptoNTV: "",
    ValidUptoTV: "",
    IssuingAuthority: "",
    TypeofLicense: "",
    BadgeNumber: "",
    PreAccidentCondition: "",
    dateOfAccident: "",
    timeOfAccident: "",
    placeOfAccident: "",
    placeOfSurvey: "",
    dateOfAllotment: "",
    dateTimeOfSurvey: "",
    dateOfSpotSurvey: "",
    causeAndNature: "",
    policeAction: "",
    detailsOfLoad: "",
    thirdPartyLoss: "",
    particularsOfLoss: "",
    decision: "",
    material: "",
    description: "",
    hsn: "",
    qty: "",
    rate: "",
    taxPercent: "",
    depPercent: "0",
    rrCharges: "0.0",
    repairCharges: "0.0",
    paintingCharges: "0.0",
    labourCharges: "0.0",
    parts: [],
    labourDetails: [
      {
        depAmount: "Not Found",
        labourType: "R and R",
        taxAmount: "Not Found",
        taxableAmount: "Not Found",
        totalAmount: "Not Found",
      },
    ],
    finalCalculation: {
      compulsoryDeductible: "Not Found",
      digitLiability: "Not Found",
      nonStandardAmount: "Not Found",
      salvageDeductions: "Not Found",
      totalLiability: "Not Found",
      towingAmount: "Not Found",
      voluntaryDeductible: "Not Found",
    },
  });

  // Modified state for validation matches
  const [validationMatches, setValidationMatches] = useState({
    chassisMatch: null,
    engineMatch: null, //  Engine Number = RC Number se compare
    chassisInspectionValue: "",
    engineInspectionValue: "", //  yh backend ka RC Number h
    shouldShowChassisValidation: true,
    shouldShowEngineValidation: true,
    policyMatch: null,
    registrationMatch: null,
    makeMatch: null,
    modelMatch: null,
    variantMatch: null,
    accidentDateMatch: null,
    driverLicenseMatch: null,
    driverNameMatch: null,

    policyInspectionValue: "",
    registrationInspectionValue: "",
    makeInspectionValue: "",
    modelInspectionValue: "",
    variantInspectionValue: "",
    accidentDateInspectionValue: "",
    driverLicenseInspectionValue: "",
    driverNameInspectionValue: "",
  });

  // Fetch car inspection data when component mounts
  useEffect(() => {
    const fetchInspectionData = async () => {
      if (claimNumber) {
        const data = await fetchCarInspectionData(claimNumber);
        setCarInspectionData(data);
        console.log("Car Inspection Data:", data);
      }
    };
    fetchInspectionData();
  }, [claimNumber]);

  // Initialize form values from the inspection API when available
  useEffect(() => {
    if (!carInspectionData) return;

    const chassisPhysicallyChecked =
      carInspectionData.chassisNumber &&
      carInspectionData.chassisNumber.toUpperCase() !== "NOT FOUND"
        ? "Yes"
        : "No";

    setFormData((prev) => ({
      ...prev,
      policyNumber: prev.policyNumber || carInspectionData.policyNumber || "",
      VehicleRegistrationNumber:
        prev.VehicleRegistrationNumber ||
        carInspectionData.registrationNumber ||
        "",
      Make: prev.Make || carInspectionData.make || "",
      Model: prev.Model || carInspectionData.model || "",
      Variant: prev.Variant || carInspectionData.variant || "",
      dateOfAccident:
        prev.dateOfAccident || carInspectionData.accidentDate || "",
      DrivingLicenseNo: sanitizeDrivingLicenseNo(
        prev.DrivingLicenseNo || carInspectionData.driverLicense || "",
      ),
      NameofDriver: prev.NameofDriver || carInspectionData.driverName || "",
      ChassisNumber:
        prev.ChassisNumber || carInspectionData.chassisNumber || "",
      EngineNumber: prev.EngineNumber || carInspectionData.engineNumber || "",
      ChassisPhysicallyChecked: chassisPhysicallyChecked || "NO",
    }));
  }, [carInspectionData]);

  useEffect(() => {
    if (!carInspectionData) return;

    console.log("Car Inspection Data for validation:", carInspectionData);

    const chassis = formData.ChassisNumber?.trim();
    const engine = formData.EngineNumber?.trim();
    const policy = formData.policyNumber?.trim();
    const registration = formData.VehicleRegistrationNumber?.trim();
    const make = formData.Make?.trim();
    const model = formData.Model?.trim();
    const variant = formData.Variant?.trim();
    const accidentDate = formData.dateOfAccident?.trim();
    const driverLicense = formData.DrivingLicenseNo?.trim();
    const driverName = formData.NameofDriver?.trim();
    const inspectionChassis = carInspectionData?.chassisNumber;
    const inspectionPolicy = carInspectionData?.policyNumber;
    const inspectionRegistration = carInspectionData?.registrationNumber;
    const inspectionMake = carInspectionData?.make;
    const inspectionModel = carInspectionData?.model;
    const inspectionVariant = carInspectionData?.variant;
    const inspectionAccidentDate = carInspectionData?.accidentDate;
    const inspectionDriverLicense = carInspectionData?.driverLicense;
    const inspectionDriverName = carInspectionData?.driverName;
    // const inspectionRC = carInspectionData?.rcNumber;
    const inspectionEngine = carInspectionData?.engineNumber;

    const updates = {};

    const checkMatch = (formValue, inspectionValue, matchKey, valueKey) => {
      if (formValue && inspectionValue) {
        const normalizedForm = normalizeMatchValue(formValue);
        const normalizedInspection = normalizeMatchValue(inspectionValue);

        updates[matchKey] = normalizedForm === normalizedInspection;

        updates[valueKey] = inspectionValue;
      }
    };

    if (chassis && inspectionChassis) {
      const isChassisNotFound = chassis.toUpperCase() === "NOT FOUND";
      const isInspectionNotFound =
        inspectionChassis.toUpperCase() === "NOT FOUND";

      if (isChassisNotFound && isInspectionNotFound) {
        updates.chassisMatch = null;
        updates.chassisInspectionValue = "";
        updates.shouldShowChassisValidation = false;
      } else if (isChassisNotFound && !isInspectionNotFound) {
        updates.chassisMatch = false;
        updates.chassisInspectionValue = inspectionChassis;
        updates.shouldShowEngineValidation = true;
      } else if (!isChassisNotFound && isInspectionNotFound) {
        updates.chassisMatch = false;
        updates.chassisInspectionValue = "NOT FOUND";
        updates.shouldShowEngineValidation = true;
      } else {
        const chassisMatch =
          chassis.toUpperCase() === inspectionChassis.toUpperCase();
        updates.chassisMatch = chassisMatch;
        updates.chassisInspectionValue = inspectionChassis;
        updates.shouldShowEngineValidation = true;
      }
    }

    // 2. Engine Validation
    if (engine && inspectionEngine) {
      const isEngineNotFound = engine.toUpperCase() === "NOT FOUND";
      const isEngineInspectionNotFound =
        inspectionEngine.toUpperCase() === "NOT FOUND";

      if (isEngineNotFound && isEngineInspectionNotFound) {
        updates.engineMatch = null;
        updates.engineInspectionValue = "";
        updates.shouldShowEngineValidation = false;
      } else if (isEngineNotFound && !isEngineInspectionNotFound) {
        updates.engineMatch = false;
        updates.engineInspectionValue = inspectionEngine;
        updates.shouldShowEngineValidation = true;
      } else if (!isEngineNotFound && isEngineInspectionNotFound) {
        updates.engineMatch = false;
        updates.engineInspectionValue = "NOT FOUND";
        updates.shouldShowEngineValidation = true;
      } else {
        const engineMatch =
          engine.toUpperCase() === inspectionEngine.toUpperCase();
        updates.engineMatch = engineMatch;
        updates.engineInspectionValue = inspectionEngine;
        updates.shouldShowEngineValidation = true;
      }
    }

    checkMatch(
      policy,
      inspectionPolicy,
      "policyMatch",
      "policyInspectionValue",
    );

    checkMatch(
      registration,
      inspectionRegistration,
      "registrationMatch",
      "registrationInspectionValue",
    );

    checkMatch(make, inspectionMake, "makeMatch", "makeInspectionValue");

    checkMatch(model, inspectionModel, "modelMatch", "modelInspectionValue");

    checkMatch(
      variant,
      inspectionVariant,
      "variantMatch",
      "variantInspectionValue",
    );

    const normalizeDate = (value) => {
      if (!value) return "";

      return new Date(value).toISOString().split("T")[0];
    };

    updates.accidentDateMatch =
      normalizeDate(accidentDate) === normalizeDate(inspectionAccidentDate);

    updates.accidentDateInspectionValue = inspectionAccidentDate;

    // checkMatch(
    //   accidentDate,
    //   inspectionAccidentDate,
    //   'accidentDateMatch',
    //   'accidentDateInspectionValue'
    // );

    checkMatch(
      driverLicense,
      inspectionDriverLicense,
      "driverLicenseMatch",
      "driverLicenseInspectionValue",
    );

    checkMatch(
      driverName,
      inspectionDriverName,
      "driverNameMatch",
      "driverNameInspectionValue",
    );

    setValidationMatches((prev) => ({ ...prev, ...updates }));
  }, [
    formData.ChassisNumber,
    formData.EngineNumber,
    formData.policyNumber,
    formData.VehicleRegistrationNumber,
    formData.Make,
    formData.Model,
    formData.Variant,
    formData.dateOfAccident,
    formData.DrivingLicenseNo,
    formData.NameofDriver,
    carInspectionData,
  ]);

  // ✅ SIMPLIFIED save function for chassis and engine validation
  // useEffect(() => {
  //   const chassisNoMatch = validationMatches.chassisMatch === false;
  //   const engineNoMatch = validationMatches.engineMatch === false;

  //   if (chassisNoMatch || engineNoMatch) {
  //     console.log('❌ Mismatch found - saving to database');

  //     let matchType = '';
  //     if (chassisNoMatch && engineNoMatch) {
  //       matchType = 'both_no_match';
  //     } else if (chassisNoMatch) {
  //       matchType = 'chassis_no_match';
  //     } else {
  //       matchType = 'engine_no_match';
  //     }

  //     saveValidationMatch(claimNumber, 'YES', matchType);
  //   } else if (validationMatches.chassisMatch === true &&
  //              validationMatches.engineMatch === true) {
  //     console.log('✅ Both match - saving NO to database');
  //     saveValidationMatch(claimNumber, 'NO', 'both_match');
  //   }
  // }, [validationMatches.chassisMatch, validationMatches.engineMatch, claimNumber]);
  useEffect(() => {
    const hasAnyValidation =
      validationMatches.chassisMatch !== null ||
      validationMatches.engineMatch !== null ||
      validationMatches.policyMatch !== null ||
      validationMatches.registrationMatch !== null ||
      validationMatches.makeMatch !== null ||
      validationMatches.modelMatch !== null ||
      validationMatches.variantMatch !== null ||
      validationMatches.accidentDateMatch !== null ||
      validationMatches.driverLicenseMatch !== null ||
      validationMatches.driverNameMatch !== null;

    // Prevent unnecessary API call
    if (!hasAnyValidation) return;

    console.log("🚀 Saving all validation statuses");

    saveValidationMatch({
      claimNumber,

      chassisMatch: validationMatches.chassisMatch,

      engineMatch: validationMatches.engineMatch,

      policyMatch: validationMatches.policyMatch,

      registrationMatch: validationMatches.registrationMatch,

      makeMatch: validationMatches.makeMatch,

      modelMatch: validationMatches.modelMatch,

      variantMatch: validationMatches.variantMatch,

      accidentDateMatch: validationMatches.accidentDateMatch,

      driverLicenseMatch: validationMatches.driverLicenseMatch,

      driverNameMatch: validationMatches.driverNameMatch,
    });
  }, [validationMatches, claimNumber]);
  // API function to save validation match
  // UpdateInfoPage.js - Update saveValidationMatch function
  const saveValidationMatch = async (payload) => {
    try {
      console.log("🚀 Saving validation:", payload);

      const response = await fetch(
        "/process_data/update-chassis-engine-match",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      console.log("✅ Validation save response:", data);
    } catch (error) {
      console.error("❌ Validation save error:", error);
    }
  };
  // const saveValidationMatch = async (claimNumber, matchStatus, matchType) => {
  //   console.log('🚀 Calling validation API with:', { claimNumber, matchStatus, matchType });

  //   try {
  //     const response = await fetch('/process_data/update-chassis-engine-match', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //     body: JSON.stringify({
  //   claimNumber,
  //   matchStatus,
  //   matchType,
  //   chassisMatch: validationMatches.chassisMatch,
  //   engineMatch: validationMatches.engineMatch
  // })
  //     });

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     const data = await response.json();
  //     console.log('✅ Validation API Response:', data);

  //   } catch (error) {
  //     console.error('❌ Validation API Error:', error);
  //   }
  // };

  useEffect(() => {
    const fetchSurveyorId = async () => {
      try {
        const response = await fetch("/process_data/get_surveyor_id", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ claimNumber: claimNumber }),
        });

        const data = await response.json();
        if (data?.surveyor_id) {
          setSurveyorId(data.surveyor_id);
          console.log("Surveyor ID:", data.surveyor_id);
        }
      } catch (error) {
        console.error("Failed to fetch surveyor ID:", error);
      }
    };

    if (claimNumber) {
      fetchSurveyorId();
    }
  }, [claimNumber]);

  const handleTabClick = (tab) => {
    // Check if current tab fields are all filled before allowing navigation
    const currentTabFields = getTabFields(activeTab);
    const isCurrentTabValid = true;

    if (isCurrentTabValid) {
      setActiveTab(tab);
      // Clear errors when changing tabs
      setErrors({});
      setShowPopup(false);
    } else {
      // Show popup instead of field-level errors
      setPopupMessage("Please fill all required fields before proceeding.");
      setShowPopup(true);

      // Set errors for empty fields (to highlight them)
      const newErrors = {};
      currentTabFields.forEach((field) => {
        if (!formData[field] || formData[field].trim() === "") {
          newErrors[field] = "This field is required";
        }
      });
      setErrors(newErrors);
    }
  };

  // Get field names for each tab
  const getTabFields = (tab) => {
    switch (tab) {
      case "insurance":
        return [
          "policyNumber",
          "insurancePeriod",
          "idv",
          "claimNumber",
          "endorsement",
          "insuranceCompany",
          "insured",
          "hpa",
          "appointedBy",
        ];
      case "vehicle":
        return [
          "VehicleRegistrationNumber",
          "RegisteredOwner",
          "OwnerSerialNoTransferDate",
          "DateofRegistration",
          "ChassisNumber",
          "EngineNumber",
          "Variant",
          "Color",
          "CubicCapacity",
          "FuelUsed",
          "OdoMeterReading",
          "PUCNumber",
          "ValidUpto",
        ];
      case "driver":
        return [
          "NameofDriver",
          "DateofBirth",
          "DrivingLicenseNo",
          "DateofIssue",
          "ValidFrom",
          "ValidUptoNTV",
          "ValidUptoTV",
          "IssuingAuthority",
          "TypeofLicense",
          "BadgeNumber",
        ];
      case "accident":
        return [
          "dateOfAccident",
          "timeOfAccident",
          "placeOfAccident",
          "placeOfSurvey",
          "dateOfAllotment",
          "dateTimeOfSurvey",
          "dateOfSpotSurvey",
          "causeAndNature",
          "policeAction",
          "detailsOfLoad",
          "thirdPartyLoss",
          "particularsOfLoss",
        ];
      case "partEntries":
        return [
          "job",
          "material",
          "description",
          "hsn",
          "qty",
          "rate",
          "taxPercent",
          "depPercent",
          "rrCharges",
          "repairCharges",
          "paintingCharges",
          "labourCharges",
        ];
      case "Estimate":
        return [
          "job",
          "material",
          "description",
          "variance",
          "qty",
          "rate",
          "taxPercent",
          "depPercent",
          "rrCharges",
          "repairCharges",
          "paintingCharges",
          "labourCharges",
        ];
      default:
        return [];
    }
  };

  // Validate if all fields in a tab are filled

  const handleContinue = (nextTab) => {
    const currentTabFields = getTabFields(activeTab);
    const isCurrentTabValid = true;

    if (isCurrentTabValid) {
      setActiveTab(nextTab);
      // Clear errors when changing tabs
      setErrors({});
      setShowPopup(false);
    } else {
      // Show popup instead of just field-level errors
      setPopupMessage("Please fill all required fields before proceeding.");
      setShowPopup(true);

      const newErrors = {};
      currentTabFields.forEach((field) => {
        const value = formData[field];
        if (!value || value.trim() === "") {
          newErrors[field] = "This field is required";
        }
      });
      setErrors(newErrors);
    }
  };

  const saveFormDataToBackend = async () => {
    const ispartEntriesTabValid = true;
    if (ispartEntriesTabValid) {
      setUploading(true); // Show "Uploading data..." pop-up
      try {
        const response = await fetch("/server/upload-claim-data", {
          // Your backend API endpoint
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`233`, data);
          // Update work_app_update to TRUE in claim_progress
          await fetch("/process_data/update_work_app_update", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ claimNumber: formData.claimNumber }),
          });

          setPopupMessage(`Claim data has been successfully uploaded! `);
          // Optional: Redirect or clear form after saving
          setFormData((prev) => ({
            ...prev,
            policyNumber: "",
            insurancePeriod: "",
            idv: "",
            claimNumber: "",
            endorsement: "",
            insuranceCompany: "",
            insured: "",
            hpa: "",
            appointedBy: "",
            SeatingCapacity: "",
            ChassisPhysicallyChecked: "",
            VehicleRegistrationNumber: "",
            RegisteredOwner: "",
            OwnerSerialNoTransferDate: "",
            DateofRegistration: "",
            ChassisNumber: "",
            EngineNumber: "",
            Variant: "",
            Model: "",
            PreAccidentCondition: "",
            Color: "",
            CubicCapacity: "",
            FuelUsed: "",
            OdoMeterReading: "",
            PUCNumber: "",
            Make: "",
            Model: "",
            BodyType: "",
            ValidUpto: "",
            NameofDriver: "",
            DateofBirth: "",
            DrivingLicenseNo: "",
            TaxParticulars: "",
            DateofIssue: "",
            ValidFrom: "",
            ValidUptoNTV: "",
            ValidUptoTV: "",
            IssuingAuthority: "",
            TypeofLicense: "",
            BadgeNumber: "",
            dateOfAccident: "",
            timeOfAccident: "",
            placeOfAccident: "",
            placeOfSurvey: "",
            dateOfAllotment: "",
            dateTimeOfSurvey: "",
            dateOfSpotSurvey: "",
            causeAndNature: "",
            policeAction: "",
            detailsOfLoad: "",
            thirdPartyLoss: "",
            particularsOfLoss: "",
            // Keep these important objects intact
            labourDetails: prev.labourDetails || [
              {
                depAmount: "Not Found",
                labourType: "R and R",
                taxAmount: "Not Found",
                taxableAmount: "Not Found",
                totalAmount: "Not Found",
              },
            ],
            finalCalculation: prev.finalCalculation || {
              compulsoryDeductible: "Not Found",
              digitLiability: "Not Found",
              nonStandardAmount: "Not Found",
              salvageDeductions: "Not Found",
              totalLiability: "Not Found",
              towingAmount: "Not Found",
              voluntaryDeductible: "Not Found",
            },
            parts: prev.parts || [],
          }));
          setTimeout(() => {
            navigate(`/initial-report/${claimNumber}`);
          }, 3000);
          setActiveTab("insurance"); // Reset to the first tab
          setErrors({});
        } else {
          const errorData = await response.json();
          setPopupMessage(
            `Failed to upload claim data. ${
              errorData.error || "Please try again."
            }`,
          );
          console.error("Backend Upload Error:", errorData);
        }
      } catch (error) {
        setPopupMessage("Failed to connect to the server.");
        console.error("Frontend Error:", error);
      } finally {
        setUploading(false); // Hide "Uploading data..." pop-up
        setShowPopup(true); // Show success/error message pop-up
      }
    } else {
      setPopupMessage(
        "Please complete all required fields in all tabs before finishing.",
      );
      setShowPopup(true);
    }
  };

  // Close the popup and focus on the first empty field
  const closePopup = () => {
    setShowPopup(false);

    // Find the first empty field and focus on it
    const currentTabFields = getTabFields(activeTab);
    for (const field of currentTabFields) {
      if (!formData[field] || formData[field].trim() === "") {
        const element = document.querySelector(`[name="${field}"]`);
        if (element) {
          element.focus();
          break;
        }
      }
    }
  };

  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);

  // Helper functions to extract date and time from datetime string
  const extractDateFromDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";
    try {
      const date = new Date(dateTimeString);
      return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD format
    } catch (error) {
      console.error("Error extracting date:", error);
      return "";
    }
  };

  const extractTimeFromDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";

    try {
      const timePart = dateTimeString.split("T")[1]?.substring(0, 5); // HH:MM
      return timePart || "";
    } catch {
      return "";
    }
  };

  const extractDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";

    const [date, time] = dateTimeString.split(" ");
    return `${date} ${time.substring(0, 5)}`;
  };

  // ✅ ADD THIS FUNCTION - formatTimeToHHMMSS (for HH:MM:SS)
  const formatTimeToHHMMSS = (timeString) => {
    if (!timeString) return "";

    // Remove any non-numeric and non-colon characters
    const cleanTime = timeString.replace(/[^0-9:]/g, "");

    // If already in HH:MM:SS format
    if (/^\d{2}:\d{2}:\d{2}$/.test(cleanTime)) {
      return cleanTime;
    }

    // If in HH:MM format, add :00 seconds
    if (/^\d{2}:\d{2}$/.test(cleanTime)) {
      return `${cleanTime}:00`;
    }

    // Try to format as HH:MM:SS from raw numbers
    if (cleanTime.length === 6) {
      // 123456 -> 12:34:56
      const hours = cleanTime.substring(0, 2);
      const minutes = cleanTime.substring(2, 4);
      const seconds = cleanTime.substring(4, 6);
      return `${hours}:${minutes}:${seconds}`;
    } else if (cleanTime.length >= 4) {
      // 1234 -> 12:34:00
      const hours = cleanTime.substring(0, 2);
      const minutes = cleanTime.substring(2, 4);
      return `${hours}:${minutes}:00`;
    }

    // Default: return as is
    return cleanTime;
  };

  const sanitizeDrivingLicenseNo = (value) => {
    if (typeof value !== "string") return value;
    return value.replace(/[^A-Za-z0-9]/g, "");
  };

  const normalizeMatchValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value)
      .trim()
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase();
  };

  useEffect(() => {
    const fetchClaimFormData = async () => {
      if (!claimNumber) return;

      try {
        const response = await axios.get(
          `/process_data/get_claim_form_data/${claimNumber}`,
        );
        const claimFormData = response.data.data;
        console.log("claim form data", claimFormData);

        if (!claimFormData) return;

        const accidentDate = extractDateFromDateTime(
          claimFormData.accidentDateTime,
        );
        const accidentTime = extractTimeFromDateTime(
          claimFormData.accidentDateTime,
        );

        const currentDate = new Date().toISOString().split("T")[0];

        let surveyLocationFromImages = "";
        let surveyDateTimeFromImages = "";

        // ----------------------------------------
        // Fetch Audit Info for Survey Location + DATETIME
        // ----------------------------------------
        try {
          const auditResponse = await axios.post(
            "/process_data/get_audit_info",
            {
              audit_id: claimNumber,
            },
          );

          const auditData = Array.isArray(auditResponse.data)
            ? auditResponse.data
            : [auditResponse.data];

          if (auditData.length > 0) {
            const image =
              auditData.find((img) => img.latitude && img.longitude) ||
              auditData[0];
            console.log("589", image);
            console.log("590", image.clicked_time);

            // LOCATION
            if (image.latitude && image.longitude) {
              surveyLocationFromImages = await getLocationFromCoords(
                image.latitude,
                image.longitude,
              );
            } else if (image.location) {
              surveyLocationFromImages = image.location;
            }

            // FULL DATE + TIME
            surveyDateTimeFromImages =
              extractDateTime(image.clicked_time) ||
              extractDateTime(image.timestamp) ||
              extractDateTime(image.created_at) ||
              extractDateTime(image.date) ||
              "";
            console.log("609", surveyDateTimeFromImages);
          }
        } catch {
          surveyLocationFromImages = claimFormData.location || "";
        }

        // ----------------------------------------------------
        // UPDATE FORM DATA
        // ----------------------------------------------------
        setFormData((prev) => ({
          ...prev,

          dateOfAccident: accidentDate,
          timeOfAccident: accidentTime ? formatTimeToHHMMSS(accidentTime) : "",
          placeOfAccident: claimFormData.location || "",
          date: claimFormData.date || "",
          causeAndNature: claimFormData.description || prev.causeAndNature,

          dateOfAllotment: currentDate,
          placeOfSurvey:
            surveyLocationFromImages || claimFormData.location || "",

          // ⭐ NOW FULL DATETIME: YYYY-MM-DD HH:MM
          dateTimeOfSurvey: surveyDateTimeFromImages
            ? surveyDateTimeFromImages + ":00"
            : "",

          // Additional mappings
          VehicleRegistrationNumber:
            claimFormData.registrationNumber || prev.VehicleRegistrationNumber,
          DrivingLicenseNo: sanitizeDrivingLicenseNo(
            claimFormData.driverLicense || prev.DrivingLicenseNo,
          ),
        }));
      } catch (error) {
        console.error("Error fetching ClaimForm data:", error);
      }
    };

    fetchClaimFormData();
  }, [claimNumber]);

  // ✅ UPDATED: fetchOCRData - PRESERVE PREVIOUS VALUES
  useEffect(() => {
    const fetchOCRData = async () => {
      if (!claimNumber) return;

      try {
        await axios.post("/process_data/update_survey_doc", { claimNumber });

        let mainData = null;

        if (claimNumber) {
          const response = await axios.post(
            `/server/get-claim-data/${claimNumber}`,
          );
          mainData = response.data;
          console.log("420", mainData);

          setData(mainData); // for UI/state reference
        }

        // 🟢 Invoice Part Rows
        if (mainData?.parts?.length > 0) {
          //         const transformedRows = mainData.parts.map(item => ({
          //           decision: item.decision || 'Not Found',
          //           material: item.material || 'Not Found',
          //           description: item.description || 'Not Found',
          //           hsn: item.hsn || 'Not Found',
          //           qty: parseFloat(item.qty) || 0,
          //           rate: parseFloat(item.rate) || 0,
          //           taxPercent: parseFloat(item.taxPercent) || 0,
          //           // depPercent: parseFloat(item.depPercent) || 0,
          //           depPercent:
          // item.depPercent &&
          // item.depPercent !== '0'
          //   ? parseFloat(item.depPercent)
          //   : 55,
          //           rrCharges: parseFloat(item.rrCharges) || 0,
          //           repairCharges: parseFloat(item.repairCharges) || 0,
          //           paintingCharges: parseFloat(item.paintingCharges) || 0,
          //           labourCharges: parseFloat(item.labourCharges) || 0,
          //           taxAmount: item.taxAmount || 0,
          //           taxableAmount: item.taxableAmount,
          //           totalAmount: item.totalAmount,
          //           depAmount: item.depAmount
          //         }))
          const transformedRows = mainData.parts.map((item) => {
            const rate = parseFloat(item.rate) || 0;

            const depPercent =
              item.depPercent && item.depPercent !== "0"
                ? parseFloat(item.depPercent)
                : 0;

            // ✅ fresh calculation
            const depAmount = (rate * depPercent) / 100;

            return {
              decision: item.decision || "Not Found",
              material: item.material || "Not Found",
              description: item.description || "Not Found",
              hsn: item.hsn || "Not Found",
              qty: parseFloat(item.qty) || 0,

              rate,

              taxPercent: parseFloat(item.taxPercent) || 0,

              depPercent,

              depAmount,

              rrCharges: parseFloat(item.rrCharges) || 0,
              repairCharges: parseFloat(item.repairCharges) || 0,
              paintingCharges: parseFloat(item.paintingCharges) || 0,
              labourCharges: parseFloat(item.labourCharges) || 0,

              taxAmount: item.taxAmount || 0,
              taxableAmount: item.taxableAmount,
              totalAmount: item.totalAmount,
            };
          });
          console.log("697", transformedRows);

          setRows(transformedRows);

          // ✅ Set formData fields if present in main response (non-parts fields)
          // ✅ IMPORTANT: Preserve previous values for survey details
          setFormData((prev) => ({
            ...prev,
            claimNumber: claimNumber,
            policyNumber: mainData.policyNumber || prev.policyNumber,
            insurancePeriod:
              normalizeToInputDateFormat(mainData.insurancePeriod) ||
              prev.insurancePeriod,

            idv: mainData.idv || prev.idv,
            endorsement: mainData.endorsement || prev.endorsement,
            insuranceCompany:
              mainData.insuranceCompany || prev.insuranceCompany,
            insured: mainData.insured || prev.insured,
            hpa: mainData.hpa || prev.hpa,
            SeatingCapacity: mainData.SeatingCapacity || prev.SeatingCapacity,
            appointedBy: mainData.appointedBy || prev.appointedBy,
            VehicleRegistrationNumber:
              mainData.VehicleRegistrationNumber ||
              prev.VehicleRegistrationNumber,
            RegisteredOwner: mainData.RegisteredOwner || prev.RegisteredOwner,
            OwnerSerialNoTransferDate:
              mainData.OwnerSerialNoTransferDate ||
              prev.OwnerSerialNoTransferDate,
            DateofRegistration:
              normalizeToInputDateFormat(mainData.DateofRegistration) ||
              prev.DateofRegistration,
            ChassisNumber: mainData.ChassisNumber || prev.ChassisNumber,
            // ChassisPhysicallyChecked:
            //   mainData.ChassisPhysicallyChecked ||
            //   prev.ChassisPhysicallyChecked,
            EngineNumber: mainData.EngineNumber || prev.EngineNumber,
            Variant: mainData.Variant || prev.Variant,
            Make: mainData.Make || prev.Make,
            TaxParticulars: mainData.TaxParticulars || prev.TaxParticulars,
            Model: mainData.Model || prev.Model,
            Color: mainData.Color || prev.Color,
            PreAccidentCondition:
              mainData.PreAccidentCondition || prev.PreAccidentCondition,
            BodyType: mainData.BodyType || prev.BodyType,
            CubicCapacity: mainData.CubicCapacity || prev.CubicCapacity,
            FuelUsed: mainData.FuelUsed || prev.FuelUsed,
            OdoMeterReading: mainData.OdoMeterReading || prev.OdoMeterReading,
            PUCNumber: mainData.PUCNumber || prev.PUCNumber,
            ValidUpto: mainData.ValidUpto || prev.ValidUpto,
            NameofDriver: mainData.NameofDriver || prev.NameofDriver,
            DateofBirth:
              normalizeToInputDateFormat(mainData.DateofBirth) ||
              prev.DateofBirth,
            DrivingLicenseNo: sanitizeDrivingLicenseNo(
              mainData.DrivingLicenseNo || prev.DrivingLicenseNo,
            ),
            DateofIssue:
              normalizeToInputDateFormat(mainData.DateofIssue) ||
              prev.DateofIssue,
            ValidFrom:
              normalizeToInputDateFormat(mainData.ValidFrom) || prev.ValidFrom,
            ValidUptoNTV:
              normalizeToInputDateFormat(mainData.ValidUptoNTV) ||
              prev.ValidUptoNTV,
            ValidUptoTV:
              normalizeToInputDateFormat(mainData.ValidUptoTV) ||
              prev.ValidUptoTV,
            IssuingAuthority:
              mainData.IssuingAuthority || prev.IssuingAuthority,
            TypeofLicense: mainData.TypeofLicense || prev.TypeofLicense,
            BadgeNumber: mainData.BadgeNumber || prev.BadgeNumber,

            // ✅ CRITICAL: Preserve accident and survey details from fetchClaimFormData
            dateOfAccident:
              prev.dateOfAccident ||
              normalizeToInputDateFormat(mainData.dateOfAccident),
            timeOfAccident: (() => {
              const time = prev.timeOfAccident || mainData.timeOfAccident || "";
              if (!time) return "";
              // Add :00 seconds if not present
              if (time && time.length === 5 && time.includes(":")) {
                return time + ":00";
              }
              return time;
            })(),
            placeOfAccident: prev.placeOfAccident || mainData.placeOfAccident,

            // ✅ CRITICAL: Preserve causeAndNature from fetchClaimFormData
            causeAndNature: prev.causeAndNature || mainData.causeAndNature,

            // ✅ CRITICAL: Preserve survey mapping fields (ESPECIALLY placeOfSurvey)
            placeOfSurvey: prev.placeOfSurvey || mainData.placeOfSurvey,
            dateOfAllotment:
              prev.dateOfAllotment ||
              normalizeToInputDateFormat(mainData.dateOfAllotment),
            dateTimeOfSurvey: (() => {
              const dt =
                prev.dateTimeOfSurvey || mainData.dateTimeOfSurvey || "";
              if (!dt) return "";
              // If in YYYY-MM-DD HH:MM format, add seconds
              if (dt.includes(" ") && dt.split(" ")[1].length === 5) {
                return dt + ":00";
              }
              return dt;
            })(),

            dateOfSpotSurvey:
              normalizeToInputDateFormat(mainData.dateOfSpotSurvey) ||
              prev.dateOfSpotSurvey,
            policeAction:
              mainData.policeAction === "Not Found"
                ? ""
                : mainData.policeAction || prev.policeAction,

            detailsOfLoad:
              mainData.detailsOfLoad === "Not Found"
                ? ""
                : mainData.detailsOfLoad || prev.detailsOfLoad,

            thirdPartyLoss:
              mainData.thirdPartyLoss === "Not Found"
                ? ""
                : mainData.thirdPartyLoss || prev.thirdPartyLoss,

            particularsOfLoss:
              mainData.particularsOfLoss === "Not Found"
                ? ""
                : mainData.particularsOfLoss || prev.particularsOfLoss,

            decision: mainData.decision || prev.decision,
            material: mainData.material || prev.material,
            description: mainData.description || prev.description,
            hsn: mainData.hsn || prev.hsn,
            qty: mainData.qty || prev.qty,
            rate: mainData.rate || prev.rate,
            taxPercent: mainData.taxPercent || prev.taxPercent,
            depPercent: mainData.depPercent || prev.depPercent,
            rrCharges: mainData.rrCharges || prev.rrCharges,
            repairCharges: mainData.repairCharges || prev.repairCharges,
            paintingCharges: mainData.paintingCharges || prev.paintingCharges,
            labourCharges: mainData.labourCharges || prev.labourCharges,
            parts: mainData.parts || prev.parts,
            labourDetails: mainData.labourDetails ||
              prev.labourDetails || [
                {
                  depAmount: "Not Found",
                  labourType: "R and R",
                  taxAmount: "Not Found",
                  taxableAmount: "Not Found",
                  totalAmount: "Not Found",
                },
              ],
            finalCalculation: mainData.finalCalculation ||
              prev.finalCalculation || {
                compulsoryDeductible: "Not Found",
                digitLiability: "Not Found",
                nonStandardAmount: "Not Found",
                salvageDeductions: "Not Found",
                totalLiability: "Not Found",
                towingAmount: "Not Found",
                voluntaryDeductible: "Not Found",
              },
          }));
        }
      } catch (err) {
        console.error("Error during OCR process:", err);
      }
    };

    fetchOCRData();
  }, [claimNumber]);

  const initialRow = {
    decision: "",
    material: "",
    description: "",
    hsn: "",
    qty: 0,
    rate: 0,
    taxPercent: 0,
    depPercent: 0,
    rrCharges: 0.0,
    repairCharges: 0.0,
    paintingCharges: 0.0,
    labourCharges: 0.0,
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      parts: rows,
    }));
  }, [rows]);

  const handleChangeParts = (index, field, value) => {
    console.log("336", formData);

    const updated = [...rows];
    updated[index][field] = [
      "qty",
      "rate",
      "taxPercent",
      "depPercent",
      "rrCharges",
      "repairCharges",
      "paintingCharges",
      "labourCharges",
      // Make these editable numeric fields
      "taxableAmount",
      "taxAmount",
      "depAmount",
      "totalAmount",
    ].includes(field)
      ? parseFloat(value) || 0
      : value;
    setRows(updated);
  };

  const addRow = () => setRows([...rows, { ...initialRow }]);

  const deleteRow = (index) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
  };

  function normalizeToInputDateFormat(dateStr) {
    if (!dateStr || typeof dateStr !== "string") return "";

    // ✅ Already in YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // ✅ Handle DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split("/");
      return `${year}-${month}-${day}`;
    }

    // ✅ Handle DD-MM-YYYY (numeric months)
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split("-");
      return `${year}-${month}-${day}`;
    }

    // ✅ Handle DD-MMM-YYYY or DD-Month-YYYY
    if (dateStr.includes("-")) {
      const [day, monthRaw, year] = dateStr.split("-");
      const monthName =
        monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1).toLowerCase();

      const months = {
        January: "01",
        February: "02",
        March: "03",
        April: "04",
        May: "05",
        June: "06",
        July: "07",
        August: "08",
        September: "09",
        October: "10",
        November: "11",
        December: "12",
        Jan: "01",
        Feb: "02",
        Mar: "03",
        Apr: "04",
        May: "05",
        Jun: "06",
        Jul: "07",
        Aug: "08",
        Sep: "09",
        Oct: "10",
        Nov: "11",
        Dec: "12",
      };

      const month = months[monthName];
      if (month) {
        return `${year}-${month}-${day.padStart(2, "0")}`;
      }
    }

    return "";
  }

  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Auto populate Model & Variant when formData is prefilled
    if (formData.Make) {
      const selectedMake = makeData.find(
        (item) => item.make.toUpperCase() === formData.Make.toUpperCase(),
      );

      setModels(selectedMake?.models || []);

      if (formData.Model) {
        const selectedModel = selectedMake?.models.find(
          (m) => m.model_name.toUpperCase() === formData.Model.toUpperCase(),
        );

        setVariants(selectedModel?.variants || []);
      }
    }
  }, [formData.Make, formData.Model, makeData]);

  const calculateDepPercent = (
    registrationDate,
    accidentDate,
    material = "",
  ) => {
    const normalizedMaterial = material.toLowerCase();

    if (
      normalizedMaterial.includes("rubber") ||
      normalizedMaterial.includes("nylon") ||
      normalizedMaterial.includes("plastic") ||
      normalizedMaterial.includes("tyre") ||
      normalizedMaterial.includes("tube") ||
      normalizedMaterial.includes("battery") ||
      normalizedMaterial.includes("airbag")
    ) {
      return 50;
    }

    if (
      normalizedMaterial.includes("fibre") ||
      normalizedMaterial.includes("fiber") ||
      normalizedMaterial.includes("fiberglass") ||
      normalizedMaterial.includes("fibre glass")
    ) {
      return 30;
    }

    if (normalizedMaterial.includes("glass")) {
      return 0;
    }

    if (!registrationDate || !accidentDate) {
      return 0;
    }

    const regDate = new Date(registrationDate);
    const accDate = new Date(accidentDate);

    // Invalid date
    if (isNaN(regDate.getTime()) || isNaN(accDate.getTime())) {
      return 0;
    }

    // Accident date registration se choti hai
    if (accDate < regDate) {
      return 0;
    }

    const diffTime = accDate - regDate;

    const ageInDays = diffTime / (1000 * 60 * 60 * 24);

    const ageInYears = ageInDays / 365;

    if (ageInDays < 180) {
      return 0;
    } else if (ageInYears < 1) {
      return 5;
    } else if (ageInYears < 2) {
      return 10;
    } else if (ageInYears < 3) {
      return 15;
    } else if (ageInYears < 4) {
      return 25;
    } else if (ageInYears < 5) {
      return 35;
    } else if (ageInYears < 10) {
      return 40;
    } else {
      return 50;
    }
  };
  useEffect(() => {
    if (formData.DateofRegistration && formData.dateOfAccident) {
      setRows((prevRows) =>
        prevRows.map((row) => {
          const depValue = calculateDepPercent(
            formData.DateofRegistration,
            formData.dateOfAccident,
            row.material,
          );

          const depAmount = ((parseFloat(row.rate) || 0) * depValue) / 100;

          return {
            ...row,

            depPercent: depValue,

            depAmount,
          };
        }),
      );
    }
  }, [formData.DateofRegistration, formData.dateOfAccident]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    // Handle datetime-local for dateTimeOfSurvey (HH:MM:SS format)
    if (type === "datetime-local" && name === "dateTimeOfSurvey") {
      // Convert datetime-local (YYYY-MM-DDTHH:MM) to YYYY-MM-DD HH:MM:SS format
      const formattedValue = value.replace("T", " ") + ":00";
      setFormData((prev) => ({ ...prev, [name]: formattedValue }));
      return;
    }

    // Handle timeOfAccident input for HH:MM:SS format
    if (name === "timeOfAccident") {
      // Format as HH:MM:SS
      const formattedTime = formatTimeToHHMMSS(value);
      setFormData((prev) => ({ ...prev, [name]: formattedTime }));
      return;
    }

    // default update with normalized Driving License values
    const updatedValue =
      name === "DrivingLicenseNo" ? sanitizeDrivingLicenseNo(value) : value;
    setFormData((prev) => ({ ...prev, [name]: updatedValue }));

    // =============== WHEN MAKE CHANGES ==================
    if (name === "Make") {
      // Reset only if user ACTUALLY changed Make
      if (value !== formData.Make) {
        const selectedMake = makeData.find((item) => item.make === value);

        setModels(selectedMake ? selectedMake.models : []);
        setVariants([]);

        // Reset Model & Variant only on manual change
        setFormData((prev) => ({
          ...prev,
          Model: "",
          Variant: "",
        }));
      }
    }

    // =============== WHEN MODEL CHANGES ==================
    if (name === "Model") {
      // Reset only if user actually changed Model
      if (value !== formData.Model) {
        const selectedMake = makeData.find(
          (item) => item.make === formData.Make,
        );
        const selectedModel = selectedMake?.models.find(
          (m) => m.model_name === value,
        );

        setVariants(selectedModel ? selectedModel.variants : []);

        // Reset variant only on manual change
        setFormData((prev) => ({ ...prev, Variant: "" }));
      }
    }

    // Clear errors
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <div className="custom-content">
      <div className="update-info-container">
        <h2 className="update-info-title">Claim Details</h2>

        {showPopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <div className="popup-header">
                <h3>
                  {popupMessage.startsWith("Failed")
                    ? "Error"
                    : popupMessage.startsWith("Please")
                      ? "Required Fields"
                      : "Success"}
                </h3>
                <button className="close-btn" onClick={closePopup}>
                  ×
                </button>
              </div>
              <div className="popup-content-info">
                <p>{popupMessage}</p>
              </div>
              <div className="popup-footer">
                <button className="popup-btn" onClick={closePopup}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Uploading Overlay */}
        {uploading && (
          <div className="popup-overlay">
            <div className="popup-container">
              <div className="popup-content-info">
                <p>Uploading data...</p>
              </div>
            </div>
          </div>
        )}

        <div className="update-info-claim-header">
          <div className="update-info-claim-field">
            <label>Surveyors Reference No.</label>
            <input type="text" value={surveyorId || "Surveyor123"} readOnly />
          </div>
          <div className="update-info-claim-field">
            <label>Claim Intimation Date</label>
            <input type="text" value={formData?.date || ""} readOnly />
          </div>
          <div className="update-info-claim-field">
            <label>Claim Number</label>
            <input type="text" value={claimNumber || ""} readOnly />
          </div>
        </div>

        {/* Tabs */}
        <div className="update-info-tabs">
          <button
            className={activeTab === "insurance" ? "active" : ""}
            onClick={() => handleTabClick("insurance")}
          >
            Insurance Details
          </button>
          <button
            className={activeTab === "vehicle" ? "active" : ""}
            onClick={() => handleTabClick("vehicle")}
          >
            Vehicle Details
          </button>
          <button
            className={activeTab === "driver" ? "active" : ""}
            onClick={() => handleTabClick("driver")}
          >
            Driver Details
          </button>
          <button
            className={activeTab === "accident" ? "active" : ""}
            onClick={() => handleTabClick("accident")}
          >
            Accident & Survey
          </button>
          <button
            className={activeTab === "partEntries" ? "active" : ""}
            onClick={() => handleTabClick("partEntries")}
          >
            Part Entries
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "insurance" && (
          <div className="vehicle-details-container">
            <div className="update-info-tab-content">
              <div className="update-info-form-group">
                <label>Policy/Cover Note No.</label>
                <input
                  type="text"
                  name="policyNumber"
                  value={formData.policyNumber}
                  onChange={handleChange}
                  className={errors.policyNumber ? "error-input" : ""}
                />
                {validationMatches.policyMatch === true ? (
                  <span style={{ color: "green", fontSize: "12px" }}>
                    ✓ Match
                  </span>
                ) : validationMatches.policyMatch === false ? (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    ✗ No Match
                  </span>
                ) : null}
              </div>

              <div className="update-info-form-group">
                <label>Insurance Period From</label>
                <input
                  type="date"
                  name="insurancePeriod"
                  value={formData.insurancePeriod}
                  onChange={handleChange}
                  className={errors.insurancePeriod ? "error-input" : ""}
                />
              </div>
              <div className="update-info-form-group">
                <label>IDV</label>
                <input
                  type="text"
                  name="idv"
                  value={formData.idv}
                  onChange={handleChange}
                  className={errors.idv ? "error-input" : ""}
                />
              </div>
              <div className="update-info-form-group">
                <label>Claim No.</label>
                <input
                  type="text"
                  name="claimNumber"
                  value={formData.claimNumber || claimNumber}
                  onChange={handleChange}
                  className={errors.claimNumber ? "error-input" : ""}
                />
              </div>
              <div className="update-info-form-group">
                <label>Endorsement</label>
                <input
                  type="text"
                  name="endorsement"
                  value={formData.endorsement}
                  onChange={handleChange}
                  className={errors.endorsement ? "error-input" : ""}
                />
              </div>
              <div className="update-info-form-group">
                <label>Insurance Company</label>
                <input
                  type="text"
                  name="insuranceCompany"
                  value={formData.insuranceCompany}
                  onChange={handleChange}
                  className={errors.insuranceCompany ? "error-input" : ""}
                />
              </div>
              <div className="update-info-form-group">
                <label>Insured</label>
                <input
                  type="text"
                  name="insured"
                  value={formData.insured}
                  onChange={handleChange}
                  className={errors.insured ? "error-input" : ""}
                />
              </div>
              <div className="update-info-form-group">
                <label>HPA</label>
                <input
                  type="text"
                  name="hpa"
                  value={formData.hpa}
                  onChange={handleChange}
                  className={errors.hpa ? "error-input" : ""}
                />
              </div>
              <div className="update-info-form-group">
                <label>Appointed By</label>
                <input
                  type="text"
                  name="appointedBy"
                  value={formData.appointedBy}
                  onChange={handleChange}
                  className={errors.appointedBy ? "error-input" : ""}
                />
              </div>
            </div>
            <div className="update-info-button-container">
              <button
                className="update-info-continue-btn"
                onClick={() => handleContinue("vehicle")}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {activeTab === "vehicle" && (
          <div className="vehicle-details-container">
            <div className="vehicle-details-grid">
              <div className="form-group">
                <label>Vehicle Registration Number</label>
                <input
                  type="text"
                  name="VehicleRegistrationNumber"
                  // value={formData?.vehicle || formData.VehicleRegistrationNumber}
                  value={formData.VehicleRegistrationNumber}
                  onChange={handleChange}
                  className={
                    errors.VehicleRegistrationNumber ? "error-input" : ""
                  }
                />
                {validationMatches.registrationMatch === true ? (
                  <span style={{ color: "green", fontSize: "12px" }}>
                    ✓ Match
                  </span>
                ) : validationMatches.registrationMatch === false ? (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    ✗ No Match
                  </span>
                ) : null}
              </div>
              <div className="form-group">
                <label>Registered Owner</label>
                <input
                  type="text"
                  name="RegisteredOwner"
                  value={formData.RegisteredOwner}
                  onChange={handleChange}
                  className={errors.RegisteredOwner ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Owner Serial No/Transfer Date</label>
                <input
                  type="text"
                  name="OwnerSerialNoTransferDate"
                  value={formData.OwnerSerialNoTransferDate}
                  onChange={handleChange}
                  className={
                    errors.OwnerSerialNoTransferDate ? "error-input" : ""
                  }
                />
              </div>
              <div className="form-group">
                <label>Date of Registration</label>
                <input
                  type="date"
                  name="DateofRegistration"
                  value={formData.DateofRegistration}
                  onChange={handleChange}
                  className={errors.DateofRegistration ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Chassis Number / VIN</label>
                <input
                  type="text"
                  name="ChassisNumber"
                  value={formData.ChassisNumber}
                  onChange={handleChange}
                />

                {/* ✅ Chassis Validation - Same logic */}

                {validationMatches.shouldShowChassisValidation &&
                  formData.ChassisNumber &&
                  validationMatches.chassisInspectionValue && (
                    <div style={{ marginTop: "4px" }}>
                      {validationMatches.chassisMatch === true ? (
                        <span style={{ color: "green", fontSize: "12px" }}>
                          ✓ Match
                        </span>
                      ) : validationMatches.chassisMatch === false ? (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          ✗ No match
                        </div>
                      ) : null}
                    </div>
                  )}
              </div>
              <div className="form-group">
                <label>Chassis No. Physically checked</label>
                <input
                  type="text"
                  name="ChassisPhysicallyChecked"
                  value={formData.ChassisPhysicallyChecked}
                  onChange={handleChange}
                  className={
                    errors.ChassisPhysicallyChecked ? "error-input" : ""
                  }
                />
              </div>

              <div className="form-group make">
                <label>Make</label>
                <input
                  type="text"
                  name="Make"
                  value={formData.Make}
                  onChange={handleChange}
                  list="make-list"
                  className={`make-model-select ${
                    errors?.Make ? "error-input" : ""
                  }`}
                  placeholder="Select Make"
                />
                {validationMatches.makeMatch === true ? (
                  <span style={{ color: "green", fontSize: "12px" }}>
                    ✓ Match
                  </span>
                ) : validationMatches.makeMatch === false ? (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    ✗ No Match
                  </span>
                ) : null}

                <datalist id="make-list">
                  {makeData.map((makeItem, idx) => (
                    <option key={idx} value={makeItem.make} />
                  ))}
                </datalist>
              </div>

              <div className="form-group model">
                <label>Model</label>
                <input
                  type="text"
                  name="Model"
                  value={formData.Model}
                  onChange={handleChange}
                  placeholder="Enter Model"
                  disabled={!formData.Make}
                  className={`make-model-input ${
                    errors?.Model ? "error-input" : ""
                  }`}
                />
                {validationMatches.modelMatch === true ? (
                  <span style={{ color: "green", fontSize: "12px" }}>
                    ✓ Match
                  </span>
                ) : validationMatches.modelMatch === false ? (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    ✗ No Match
                  </span>
                ) : null}
              </div>

              <div className="form-group variant">
                <label>Variant</label>
                <input
                  type="text"
                  name="Variant"
                  value={formData.Variant}
                  onChange={handleChange}
                  placeholder="Enter Variant"
                  disabled={!formData.Model}
                  className={`make-model-input ${
                    errors?.Variant ? "error-input" : ""
                  }`}
                />
                {validationMatches.variantMatch === true ? (
                  <span style={{ color: "green", fontSize: "12px" }}>
                    ✓ Match
                  </span>
                ) : validationMatches.variantMatch === false ? (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    ✗ No Match
                  </span>
                ) : null}
              </div>

              <div className="form-group">
                <label>Engine Number</label>
                <input
                  type="text"
                  name="EngineNumber"
                  value={formData.EngineNumber}
                  onChange={handleChange}
                />

                {/* ✅ Engine Number = Engine Number से compare होगा */}
                {validationMatches.shouldShowEngineValidation &&
                  validationMatches.engineMatch === false && (
                    <div
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      ✗ No match
                    </div>
                  )}

                {validationMatches.shouldShowEngineValidation &&
                  validationMatches.engineMatch === true && (
                    <div
                      style={{
                        color: "green",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      ✓ Match
                    </div>
                  )}
              </div>
              <div className="form-group">
                <label>Color</label>
                <input
                  type="text"
                  name="Color"
                  value={formData.Color}
                  onChange={handleChange}
                  className={errors.Color ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Type of Body/Vehicle Class</label>
                <input
                  type="text"
                  name="VehicleClass"
                  value={formData.BodyType}
                  onChange={handleChange}
                  className={errors.BodyType ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Pre-Accident Condition</label>
                <input
                  type="text"
                  name="PreAccidentCondition"
                  value={formData.PreAccidentCondition}
                  onChange={handleChange}
                  className={errors.PreAccidentCondition ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Seating Capacity</label>
                <input
                  type="text"
                  name="SeatingCapacity"
                  value={formData.SeatingCapacity}
                  onChange={handleChange}
                  className={errors.SeatingCapacity ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Cubic Capacity</label>
                <input
                  type="text"
                  name="CubicCapacity"
                  value={formData.CubicCapacity}
                  onChange={handleChange}
                  className={errors.CubicCapacity ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Fuel Used</label>
                <input
                  type="text"
                  name="FuelUsed"
                  value={formData.FuelUsed}
                  onChange={handleChange}
                  className={errors.FuelUsed ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Tax Particulars</label>
                <input
                  type="text"
                  name="TaxParticulars"
                  value={formData.TaxParticulars}
                  onChange={handleChange}
                  className={errors.TaxParticulars ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Odometer Reading</label>
                <input
                  type="text"
                  name="OdoMeterReading"
                  value={carInspectionData?.odometerImageValue}
                  // value={carInspectionData?.odometerNumber}
                  onChange={handleChange}
                  className={errors.OdoMeterReading ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Valid Upto</label>
                <input
                  type="text"
                  name="ValidUpto"
                  value={formData.ValidUpto}
                  onChange={handleChange}
                  className={errors.ValidUpto ? "error-input" : ""}
                />
              </div>
            </div>
            <div className="update-info-button-container">
              <button
                className="update-info-continue-btn"
                onClick={() => handleContinue("driver")}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {activeTab === "driver" && (
          <div className="vehicle-details-container">
            <div className="vehicle-details-grid">
              <div className="form-group">
                <label>Name of Driver</label>
                <input
                  type="text"
                  name="NameofDriver"
                  value={formData.NameofDriver}
                  onChange={handleChange}
                  className={errors.NameofDriver ? "error-input" : ""}
                />
                {validationMatches.driverNameMatch === true ? (
                  <span style={{ color: "green", fontSize: "12px" }}>
                    ✓ Match
                  </span>
                ) : validationMatches.driverNameMatch === false ? (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    ✗ No Match
                  </span>
                ) : null}
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  name="DateofBirth"
                  value={formData.DateofBirth}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                  className={errors.DateofBirth ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Driving License No</label>
                <input
                  type="text"
                  name="DrivingLicenseNo"
                  value={formData.DrivingLicenseNo}
                  onChange={handleChange}
                  className={errors.DrivingLicenseNo ? "error-input" : ""}
                />
                {validationMatches.driverLicenseMatch === true ? (
                  <span style={{ color: "green", fontSize: "12px" }}>
                    ✓ Match
                  </span>
                ) : validationMatches.driverLicenseMatch === false ? (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    ✗ No Match
                  </span>
                ) : null}
              </div>
              <div className="form-group">
                <label>Date of Issue</label>
                <input
                  type="date"
                  name="DateofIssue"
                  value={formData.DateofIssue}
                  onChange={handleChange}
                  className={errors.DateofIssue ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Valid From</label>
                <input
                  type="date"
                  name="ValidFrom"
                  value={formData.ValidFrom}
                  onChange={handleChange}
                  className={errors.ValidFrom ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Valid Upto (NTV)</label>
                <input
                  type="date"
                  name="ValidUptoNTV"
                  value={formData.ValidUptoNTV}
                  onChange={handleChange}
                  className={errors.ValidUptoNTV ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Valid Upto (TV)</label>
                <input
                  type="date"
                  name="ValidUptoTV"
                  value={formData.ValidUptoTV}
                  onChange={handleChange}
                  className={errors.ValidUptoTV ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Issuing Authority</label>
                <input
                  type="text"
                  name="IssuingAuthority"
                  value={formData.IssuingAuthority}
                  onChange={handleChange}
                  className={errors.IssuingAuthority ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Type of License</label>
                <input
                  type="text"
                  name="TypeofLicense"
                  value={formData.TypeofLicense}
                  onChange={handleChange}
                  className={errors.TypeofLicense ? "error-input" : ""}
                />
              </div>
              <div className="form-group">
                <label>Badge Number</label>
                <input
                  type="text"
                  name="BadgeNumber"
                  value={formData.BadgeNumber}
                  onChange={handleChange}
                  className={errors.BadgeNumber ? "error-input" : ""}
                />
              </div>
            </div>
            <div className="update-info-button-container">
              <button
                className="update-info-continue-btn"
                onClick={() => handleContinue("accident")}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {activeTab === "accident" && (
          <div className="vehicle-details-container">
            <div className="accident-survey-grid">
              {/* First line - Date, Time, Place info */}
              <div className="accident-survey-row">
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Accident Date</label>
                    <input
                      type="date"
                      name="dateOfAccident"
                      value={formData.dateOfAccident || ""}
                      onChange={handleChange}
                      className={errors.dateOfAccident ? "error-input" : ""}
                    />
                    {validationMatches.accidentDateMatch === true ? (
                      <span style={{ color: "green", fontSize: "12px" }}>
                        ✓ Match
                      </span>
                    ) : validationMatches.accidentDateMatch === false ? (
                      <span style={{ color: "red", fontSize: "12px" }}>
                        ✗ No Match
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label> Accident Time</label>
                    <input
                      type="text"
                      name="timeOfAccident"
                      value={formData.timeOfAccident || ""}
                      onChange={handleChange}
                      placeholder="HH:MM:SS (24-hour)"
                      className={errors.timeOfAccident ? "error-input" : ""}
                      maxLength="8"
                      pattern="([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]"
                      title="Enter time in HH:MM:SS format (24-hour)"
                    />
                    <small style={{ fontSize: "12px", color: "#666" }}>
                      Format: HH:MM:SS (24-hour)
                    </small>
                  </div>
                </div>
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Place of Accident</label>
                    <input
                      type="text"
                      name="placeOfAccident"
                      value={formData.placeOfAccident || ""}
                      onChange={handleChange}
                      className={errors.placeOfAccident ? "error-input" : ""}
                    />
                  </div>
                </div>
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Place of Survey</label>
                    <input
                      type="text"
                      name="placeOfSurvey"
                      value={formData.placeOfSurvey || ""}
                      onChange={handleChange}
                      className={errors.placeOfSurvey ? "error-input" : ""}
                    />
                  </div>
                </div>
              </div>

              {/* Second line - Survey Dates */}
              <div className="accident-survey-row">
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Date of Allotment of Survey</label>
                    <input
                      type="date"
                      name="dateOfAllotment"
                      value={(() => {
                        const rawDate = formData?.date;

                        if (!rawDate) return "";

                        const [d, m, y] = rawDate.split(" ")[0].split("-");
                        return `${y}-${m}-${d}`; // convert to YYYY-MM-DD
                      })()}
                      onChange={handleChange}
                      className={errors.dateOfAllotment ? "error-input" : ""}
                    />
                  </div>
                </div>
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Date and Time of Survey</label>
                    <input
                      type="text"
                      name="dateTimeOfSurvey"
                      value={
                        formData.dateTimeOfSurvey
                          ? formData.dateTimeOfSurvey.replace(
                              /^(\d{4})-(\d{2})-(\d{2}) (.*)$/,
                              "$3-$2-$1 $4",
                            )
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        // Convert DD-MM-YYYY back to YYYY-MM-DD
                        const converted = value.replace(
                          /^(\d{2})-(\d{2})-(\d{4}) (.*)$/,
                          "$3-$2-$1 $4",
                        );
                        setFormData((prev) => ({
                          ...prev,
                          dateTimeOfSurvey: converted,
                        }));
                      }}
                      placeholder="DD-MM-YYYY HH:MM:SS"
                      className={errors.dateTimeOfSurvey ? "error-input" : ""}
                    />
                  </div>
                </div>
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Date of Receipt of Spot Survey</label>
                    <input
                      type="date"
                      name="dateOfSpotSurvey"
                      value={formData.dateOfSpotSurvey}
                      onChange={handleChange}
                      className={errors.dateOfSpotSurvey ? "error-input" : ""}
                    />
                  </div>
                </div>
              </div>

              {/* Third line - Cause and Nature of Accident */}
              <div className="accident-survey-row full-width">
                <div className="form-group">
                  <label>Cause and Nature of Accident</label>
                  <textarea
                    name="causeAndNature"
                    value={formData.causeAndNature || ""}
                    onChange={handleChange}
                    className={errors.causeAndNature ? "error-input" : ""}
                    rows="3"
                  />
                </div>
              </div>

              {/* Fourth line - Police, Load, Third Party */}
              <div className="accident-survey-row">
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Police Action</label>
                    <input
                      type="text"
                      name="policeAction"
                      value={formData.policeAction}
                      onChange={handleChange}
                      className={errors.policeAction ? "error-input" : ""}
                    />
                  </div>
                </div>
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Details of Load/Passenger</label>
                    <input
                      type="text"
                      name="detailsOfLoad"
                      value={formData.detailsOfLoad}
                      onChange={handleChange}
                      className={errors.detailsOfLoad ? "error-input" : ""}
                    />
                  </div>
                </div>
                <div className="accident-survey-column">
                  <div className="form-group">
                    <label>Third Party Loss/Injuries</label>
                    <input
                      type="text"
                      name="thirdPartyLoss"
                      value={formData.thirdPartyLoss}
                      onChange={handleChange}
                      className={errors.thirdPartyLoss ? "error-input" : ""}
                    />
                  </div>
                </div>
              </div>

              {/* Fifth line - Particulars of Loss/Damages */}
              <div className="accident-survey-row full-width">
                <div className="form-group">
                  <label>Particulars of Loss/Damages</label>
                  <textarea
                    name="particularsOfLoss"
                    value={formData.particularsOfLoss}
                    onChange={handleChange}
                    className={errors.particularsOfLoss ? "error-input" : ""}
                    rows="3"
                  />
                </div>
              </div>
            </div>

            <div className="update-info-button-container">
              <button
                className="update-info-continue-btn"
                onClick={() => handleContinue("partEntries")}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {activeTab === "partEntries" && (
          <div className="claim-container-part">
            <table className="claim-table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>JOB</th>
                  <th>MATERIAL</th>
                  <th>DESCRIPTION</th>
                  <th>HSN</th>
                  <th>QTY</th>
                  <th>PART RATE</th>
                  <th>TAXABLE AMOUNT</th>
                  <th>TAX (%)</th>
                  <th>TAX AMOUNT</th>
                  <th>TOTAL AMOUNT</th>
                  <th>DEP (%)</th>
                  <th>DEP AMOUNT</th>
                  <th>R&R CHARGES</th>
                  <th>REPAIR CHARGES</th>
                  <th>PAINTING CHARGES</th>
                  <th>LABOUR CHARGES</th>
                  <th></th> {/* Empty th for delete column */}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  return (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>
                        <select
                          value={row.decision}
                          onChange={(e) =>
                            handleChangeParts(i, "decision", e.target.value)
                          }
                        >
                          {/* Always show current decision */}
                          {["Repair", "Replace"].includes(row.decision) && (
                            <option value={row.decision} disabled>
                              {row.decision}
                            </option>
                          )}

                          {/* If decision is Replace → allow Repair */}
                          {row.decision === "Replace" && (
                            <option value="Repair">Repair</option>
                          )}

                          {/* If decision is Repair → allow Replace */}
                          {row.decision === "Repair" && (
                            <option value="Replace">Replace</option>
                          )}

                          {/* If decision is something else (NOT FOUND etc.) */}
                          {!["Repair", "Replace"].includes(row.decision) && (
                            <>
                              <option value="Repair">Repair</option>
                              <option value="Replace">Replace</option>
                            </>
                          )}
                        </select>
                      </td>

                      <td>
                        <input
                          value={row.material}
                          onChange={(e) =>
                            handleChangeParts(i, "material", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={row.description}
                          onChange={(e) =>
                            handleChangeParts(i, "description", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={row.hsn}
                          onChange={(e) =>
                            handleChangeParts(i, "hsn", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.qty}
                          onChange={(e) =>
                            handleChangeParts(i, "qty", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.rate}
                          onChange={(e) =>
                            handleChangeParts(i, "rate", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.taxableAmount ?? 0}
                          onChange={(e) =>
                            handleChangeParts(
                              i,
                              "taxableAmount",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.taxPercent}
                          onChange={(e) =>
                            handleChangeParts(i, "taxPercent", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.taxAmount ?? 0}
                          onChange={(e) =>
                            handleChangeParts(i, "taxAmount", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.totalAmount ?? 0}
                          onChange={(e) =>
                            handleChangeParts(i, "totalAmount", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.depPercent}
                          onChange={(e) =>
                            handleChangeParts(i, "depPercent", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.depAmount ?? 0}
                          onChange={(e) =>
                            handleChangeParts(i, "depAmount", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row?.rrCharges}
                          onChange={(e) =>
                            handleChangeParts(i, "rrCharges", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.repairCharges}
                          onChange={(e) =>
                            handleChangeParts(
                              i,
                              "repairCharges",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.paintingCharges}
                          onChange={(e) =>
                            handleChangeParts(
                              i,
                              "paintingCharges",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.labourCharges}
                          onChange={(e) =>
                            handleChangeParts(
                              i,
                              "labourCharges",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <button
                          className="delete-btn"
                          onClick={() => deleteRow(i)}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <button className="add-parts" onClick={addRow}>
              + ADD Parts
            </button>

            <div className="update-info-button-container">
              <button
                className="update-info-continue-btn"
                // onClick={() => handleContinue('estimate')}
                onClick={saveFormDataToBackend}
                style={{ marginRight: "10px" }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}
        {activeTab === "estimate" && (
          <div className="claim-container-part">
            <div className="estimate-container">
              <div className="parts-summary-table estimate"></div>

              {/* <PartsLabourTable /> */}

              <div className="update-info-button-container">
                <button
                  className="update-info-submit-btn"
                  onClick={saveFormDataToBackend}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Finish & Submit →"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateInfoPage;
